import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Reveal } from "@/components/motion/Reveal";

interface SectionHeadingProps {
  title: string;
  intro?: string;
  icon?: LucideIcon;
  iconClassName?: string;
  eyebrow?: string;
  as?: "h2" | "h3";
  className?: string;
}

/**
 * Consistent section heading used across resource, service, and detail pages.
 * Pairs a gold eyebrow with an H2/H3 and optional intro and icon.
 */
export const SectionHeading = ({
  title,
  intro,
  icon: Icon,
  iconClassName = "text-gold",
  eyebrow,
  as: Tag = "h2",
  className,
}: SectionHeadingProps) => (
  <Reveal>
    <header className={cn("mb-6", className)}>
      {eyebrow && (
        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-2">
          {eyebrow}
        </div>
      )}
      <div className="flex items-start gap-2.5">
        {Icon && <Icon className={cn("w-5 h-5 mt-1 shrink-0", iconClassName)} />}
        <Tag className="font-heading text-2xl md:text-3xl font-bold text-navy leading-tight">
          {title}
        </Tag>
      </div>
      {intro && (
        <p className="text-base md:text-lg text-foreground/75 leading-relaxed mt-3 max-w-3xl">
          {intro}
        </p>
      )}
    </header>
  </Reveal>
);
